import React from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCart } from '../../context/CartContext';
import { useTranslation } from 'react-i18next';
import {
  getProductImageUrl,
  handleProductImageError,
} from '../../utils/productImage';

interface CartPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartPopup: React.FC<CartPopupProps> = ({ isOpen, onClose }) => {
  const { items, totalItems, subtotal } = useCart();
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className='absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-[1000]'>
      {/* Triangle pointer - hide on mobile screens */}
      <div className='absolute -top-2 right-3 w-4 h-4 bg-white border-t border-l border-gray-200 transform rotate-45 hidden sm:block'></div>

      <div className='p-3 border-b flex justify-between items-center'>
        <h3 className='font-medium text-sm'>
          {t('cart.yourCart')} ({totalItems})
        </h3>
        <button onClick={onClose} className='text-gray-500 hover:text-gray-700'>
          <XMarkIcon className='h-4 w-4' />
        </button>
      </div>

      {items.length === 0 ? (
        <div className='p-4 text-center'>
          <p className='text-gray-500 text-sm'>{t('cart.emptyCart')}</p>
          <Link
            to='/products'
            className='mt-2 inline-block text-primary text-sm font-medium hover:underline'
            onClick={onClose}
          >
            {t('cart.continueShopping')}
          </Link>
        </div>
      ) : (
        <>
          <div className='max-h-60 overflow-y-auto divide-y divide-gray-100'>
            {items.slice(0, 3).map((item) => (
              <div key={item.id} className='p-3 flex items-center space-x-3'>
                <img
                  src={getProductImageUrl(item.image)}
                  alt={item.name}
                  className='w-12 h-12 object-cover rounded'
                  onError={handleProductImageError}
                />
                <div className='flex-1 min-w-0'>
                  <h4 className='text-xs font-medium text-gray-900 truncate'>
                    {item.name}
                  </h4>
                  <p className='text-xs text-gray-500'>
                    {item.quantity} x{' '}
                    {new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND',
                    }).format(item.price)}
                  </p>
                </div>
              </div>
            ))}

            {items.length > 3 && (
              <p className='text-xs text-gray-500 text-center py-2'>
                +{items.length - 3}{' '}
                {t('cart.itemsInCart', { count: items.length - 3 })}
              </p>
            )}
          </div>

          <div className='p-3 bg-gray-50'>
            <div className='flex justify-between mb-3'>
              <span className='text-sm'>{t('cart.subtotal')}:</span>
              <span className='font-medium text-sm'>
                {new Intl.NumberFormat('vi-VN', {
                  style: 'currency',
                  currency: 'VND',
                }).format(subtotal)}
              </span>
            </div>

            <div className='flex space-x-2'>
              <button
                onClick={onClose}
                className='flex-1 py-1.5 px-3 border border-primary text-primary bg-white hover:bg-primary/5 rounded-full text-xs font-medium transition-colors'
              >
                {t('cart.continueShopping')}
              </button>
              <Link
                to='/cart'
                onClick={onClose}
                className='flex-1 py-1.5 px-3 bg-primary text-white hover:bg-primary-dark rounded-full text-xs font-medium text-center transition-colors'
              >
                {t('common.cart')}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CartPopup;
